import { WppDivider, WppIconAttach, WppTooltip } from '@wppopen/components-library-react'
import { AnalyticsActionType } from '@wppopen/core'
import clsx from 'clsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useToast } from 'toast'

import { usePostProjectSync } from 'api/querries/usePostSync'
import { useProjectDetailed } from 'api/querries/useProjectDetailed'
import ErrorIcon from 'assets/icons/error.svg'
import ResyncIcon from 'assets/icons/resync.svg'
import SuccessIcon from 'assets/icons/success.svg'
import WarningIcon from 'assets/icons/warning.svg'
import { Button } from 'components/common/button/Button'
import TruncatedText from 'components/truncatedText/TruncatedText'
import WarningBanner from 'components/warningBanner/WarningBanner'
import config, { API_TYPES } from 'constants/api-config'
import {
  attachmentsUpdated,
  compareUpdatedFields,
  generateExpectedDueDateStatusMessage,
  generateFieldStatus,
  generateStatusMessage,
  getDeletedDocuments,
  getExpectedDueDateStatus,
  getLatestSyncedBrief,
  getLatestVersion,
  isNonProjectBrief,
  isSuccessResponse,
} from 'helpers/projectStatusHelper'
import { useUserRole } from 'providers/ApiProvider'
import { Project, StatusMessage, SyncAction, SyncStatus } from 'types/Project'
import { ChangeItem, UhubChanges } from 'types/UhubChanges'
import { formatDate } from 'utils'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './ProjectsListDetailedView.module.scss'

interface Props {
  isOpen?: boolean
  project: Project // uhub project data in this case
}

const ProjectsListDetailedView = ({ isOpen = false, project }: Props) => {
  const {
    id,
    uhubResponse,
    uhubLead, // uhub owner name
    uhubAssignee, //uhub email
    //billingEntity,
    syncStatus: statusType,
  } = project
  const [newUhubId, setNewUhubId] = useState<string>('')
  const [newUhubPreview, setNewUhubPreview] = useState<UhubChanges>()
  const { mutateAsync: retrySync, isPending: isSyncInProgress } = usePostProjectSync()

  const wrapperRef = useRef<HTMLDivElement>(null)

  const { data: projects, isFetched } = useProjectDetailed({ params: { id }, enabled: isOpen })
  const { showToast } = useToast()

  // Latest workfront project version
  const workfrontProject = useMemo(() => getLatestVersion(projects), [projects])

  const latestSyncedBriefDocuments = useMemo(() => getLatestSyncedBrief(projects, project), [projects, project])

  const updatedFields = useMemo(() => compareUpdatedFields(workfrontProject, project), [workfrontProject, project])

  const areAttachmentsUpdated = attachmentsUpdated(project, projects)

  const deletedDocuments = areAttachmentsUpdated ? getDeletedDocuments(project, projects) : undefined

  const handleSyncProject = async ({ id, actionType }: { id: string; actionType: SyncAction }) => {
    try {
      await retrySync({ id, actionType, newUhubId })
    } catch (err) {
      showToast({ message: 'Error updating the project', type: 'error' })
    }
  }
  useEffect(() => {
    const wrapper = wrapperRef?.current

    if (wrapper && isFetched) {
      if (isOpen) {
        wrapper.style.maxHeight = `${wrapper.scrollHeight + 550}px`
      } else {
        wrapper.style.maxHeight = '0'
      }
    }
  }, [isOpen, isFetched])

  const user = useUserRole()
  const newDescription = newUhubPreview?.changes.find(({ field }) => field === 'description')?.actualValue
  const isStudioXAdmin = useMemo(
    () => user?.permissions?.some((permission: string) => permission === 'fusion:sx-admin'),
    [user],
  )

  return (
    <div
      ref={wrapperRef}
      className={clsx(styles.wrapper, {
        [styles.visible]: isOpen,
      })}
    >
      <div className={styles.collapsibleContent}>
        {isOpen && statusType === SyncStatus.WARNING && (isStudioXAdmin || user?.email === uhubAssignee) && (
          <WarningBanner
            id={id}
            onSyncAction={(id, actionType) => handleSyncProject({ id, actionType })}
            isPending={isSyncInProgress}
            projectName={project.name}
            onSetNewUhubId={setNewUhubId}
            onSetNewUhubPreview={setNewUhubPreview}
          />
        )}
        <div className="grid grid-cols-3 gap-4">
          <Item
            status={workfrontProject?.projectLead?.name ? 'success' : 'error'}
            heading="Workfront Project Owner"
            value={workfrontProject?.projectLead?.name}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_16688')}
          />
          <Item
            status={generateFieldStatus('regionalOperatingUnit', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('regionalOperatingUnit', workfrontProject, project)}
            heading="Operating Unit"
            value={workfrontProject?.regionalOperatingUnit}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'assignee')}
          />
          {/* <Item
            status={generateFieldStatus('POTags', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('regionalOperatingUnit', workfrontProject, project)}
            heading="PO"
            value={workfrontProject?.POTags?.join(', ')}
          /> */}

          <Item
            status={workfrontProject?.projectLead?.emailAddr ? 'success' : 'error'}
            heading="Workfront Owner profile email"
            value={workfrontProject?.projectLead?.emailAddr}
            isEmail
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'assignee')}
          />
          <Item
            status={generateFieldStatus('briefType', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('briefType', workfrontProject, project)}
            heading="Brief Type"
            value={workfrontProject?.briefType}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_15735')}
          />
          {/* <Item
            status={generateFieldStatus('budgetCurrency', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('budgetCurrency', workfrontProject, project)}
            heading="Budget Currency"
            value={workfrontProject?.budgetCurrency}
          /> */}

          <Item
            status={generateFieldStatus('workfrontId', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('workfrontId', workfrontProject, project)}
            heading="Workfront ID"
            value={workfrontProject?.workfrontId}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_18014')}
          />
          <Item
            status={generateFieldStatus('globalCategory', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('globalCategory', workfrontProject, project)}
            heading="Category"
            value={workfrontProject?.globalCategory?.join(', ')}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_16778')}
          />
          {/* <Item
            status={generateFieldStatus('budget', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('budget', workfrontProject, project)}
            heading="Budget Assigned"
            value={workfrontProject?.budget}
          /> */}

          <Item
            status={uhubLead ? 'success' : 'error'}
            heading="Uhub project owner"
            value={uhubLead}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_30197')}
          />
          <Item
            status={generateFieldStatus('brand', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('brand', workfrontProject, project)}
            heading="Brand"
            value={workfrontProject?.brand?.join(', ')}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_15900')}
          />
          {/* TODO: this is probably not a proper field. Check which value should be displayed here */}
          {/* <Item
            status={generateFieldStatus('budget', updatedFields, workfrontProject)}
            heading="TOTAL PRODUCTION & DEVELOPMENT BUDGET"
            value={workfrontProject?.budget}
          /> */}

          <Item
            status={uhubAssignee ? 'success' : 'error'}
            heading="Uhub owner profile email"
            value={uhubAssignee}
            isEmail
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'assignee')}
          />
          <Item
            status={generateFieldStatus('marketsSelected', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('marketsSelected', workfrontProject, project)}
            heading="OU Markets"
            value={workfrontProject?.marketsSelected?.join(', ')}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_16310')}
          />
          {/* <Item
            status={generateFieldStatus('budgetYear', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('budgetYear', workfrontProject, project)}
            heading="Budget year"
            value={workfrontProject?.budgetYear}
          /> */}

          <Item
            status={generateFieldStatus('name', updatedFields, workfrontProject)}
            warningMessage={generateStatusMessage('name', workfrontProject, project)}
            heading="Project Name"
            value={workfrontProject?.name}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'summary')}
          />
          {/**
           * Here the logic is ugly and anti-pattern.
           * Currently, we do not have a flag if it's a project or a non-project,
           * therefore we rely on the targetInMarketDate definition In a nutshell:
           * Non-project briefs do not have a targetInMarketDate, therefore
           * we need to display and calculate the date from brief layer which is
           * expectedDueDateBriefs
           */}
          {!isNonProjectBrief(project) && (
            <>
              <Item
                status={generateFieldStatus('targetInMarketDate', updatedFields, workfrontProject)}
                warningMessage={generateStatusMessage('targetInMarketDate', workfrontProject, project)}
                heading="Target in Market Date"
                value={formatDate(workfrontProject?.targetInMarketDate)}
              />
              {/* For grid layout alignment */}
              <div />
            </>
          )}

          <Item
            status={new Date(workfrontProject.expectedBriefDate).getTime() < Date.now() ? 'success' : 'warning'}
            heading="Expected Brief Date"
            value={formatDate(workfrontProject.expectedBriefDate)}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_16553')}
          />
          <Item
            status={getExpectedDueDateStatus(projects, project)}
            warningMessage={generateExpectedDueDateStatusMessage(projects, project)}
            heading="Expected Brief Due Date"
            value={formatDate(latestSyncedBriefDocuments?.briefs?.[0]?.expectedDueDateBriefs)}
            replacementValue={newUhubPreview?.changes.find(({ field }) => field === 'customfield_16896')}
          />
          {/* <Item
            status={billingEntity ? 'success' : 'error'}
            heading="billing entity (project statement)/po form"
            value={billingEntity}
          /> */}

          {/* <Item
            status={generateFieldStatus('experienceId', updatedFields, workfrontProject)}
            heading="Experience ID"
            value={workfrontProject?.experienceId}
          /> */}
          {/* <Item status="error" heading="Connected to another workfrontProject?" value="Data not available" /> */}
        </div>
        <div className={styles.descriptionWrapper}>
          <div className={styles.rowHeading}>Description:</div>
          <p>
            {!!newDescription && 'Old value: '}
            {workfrontProject?.initiativeDescription || workfrontProject?.description || 'No description available.'}
          </p>
          {!!newDescription && <p>New value: {newDescription}</p>}
        </div>
        <div className="my-5">
          <WppDivider />
        </div>
        <div className={clsx(styles.footer, { [styles.hasBriefs]: latestSyncedBriefDocuments?.briefs?.length })}>
          {!!latestSyncedBriefDocuments?.briefs?.length && (
            <>
              <div className="flex flex-row min-w-fit">
                <div className={styles.briefingDocumentsHeading}>
                  <div className="flex">
                    <div>
                      {!areAttachmentsUpdated ? (
                        <img className="max-w-[16px] mr-2" src={SuccessIcon} alt="success" />
                      ) : (
                        project.syncStatus === SyncStatus.WARNING && (
                          <WppTooltip config={{ allowHTML: true, placement: 'top' }}>
                            <img src={WarningIcon} alt="warning" className="mr-2" style={{ maxWidth: '16px' }} />
                            <div
                              slot="tooltip-content"
                              className={clsx(styles.statusTooltip, {
                                [styles.statusWarning]: areAttachmentsUpdated,
                              })}
                            >
                              <span className={clsx('text-[14px]', styles.statusTooltip)}>Documents updated</span>
                            </div>
                          </WppTooltip>
                        )
                      )}
                    </div>
                    <div>BRIEFING DOCUMENTS:</div>
                  </div>
                </div>
                <div className={styles.footerGrid}>
                  <div className={styles.briefingDocuments}>
                    {latestSyncedBriefDocuments.briefs.map(brief => {
                      return brief.documents.map(({ id, name }) => {
                        return (
                          <div className="min-w-fit" key={id}>
                            <AttachmentButton label={name} documentId={id} projectName={project.name} />
                          </div>
                        )
                      })
                    })}
                    {areAttachmentsUpdated &&
                      deletedDocuments?.map(d => (
                        <div className="min-w-fit" key={id}>
                          <AttachmentButton
                            label={d.name}
                            documentId={d.id}
                            projectName={project.name}
                            isDisabled={true}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex flex-col min-w-fit">
            <Button
              variant="tertiary"
              text="Open Workfront"
              className="my-2"
              onClick={() => {
                window.open(
                  `${process.env.REACT_APP_WORKFRONT_URL}/project/${workfrontProject?.workfrontProjectId}`,
                  '_blank',
                )
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: ANALYTICS_EVENTS.DETAILED_ITEM_WORKFRONT_BUTTON_CLICKED(project.name),
                })
              }}
            />
            <Button
              disabled={!isSuccessResponse(uhubResponse)}
              variant="tertiary"
              text="Open in UHUB"
              className="my-2"
              onClick={() => {
                window.open(`${process.env.REACT_APP_UHUB_URL}/browse/${'key' in uhubResponse && uhubResponse.key}`)
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: ANALYTICS_EVENTS.DETAILED_ITEM_UHUB_BUTTON_CLICKED(project.name),
                })
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProjectsListDetailedView

const Item = ({
  heading,
  value,
  status = 'success',
  isEmail,
  warningMessage,
  replacementValue,
}: {
  heading: string
  value?: string | number | JSX.Element
  status?: 'success' | 'error' | 'warning'
  isEmail?: boolean
  warningMessage?: StatusMessage
  replacementValue?: ChangeItem
}) => {
  function getFormattedNewValue(value: string | string[] | null | undefined) {
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    if (new Date(value as string) !== null && !isNaN(new Date(value as string).getTime())) {
      return formatDate(value as string)
    }
    return value
  }
  return (
    <div className={styles.detailedItem}>
      <div className="flex flex-col">
        <div className={styles.rowHeading}>{heading}:</div>
        {!value && (
          <div
            className={clsx(
              styles.dataMissing && {
                [styles.warning]: status === 'warning',
              },
            )}
          >
            Not defined.
          </div>
        )}

        {replacementValue?.actualValue && (
          <div>
            {'Old value: '}
            {isEmail ? <a href={`mailto:${value}`}>{value}</a> : value}
          </div>
        )}
        <div>
          {replacementValue?.actualValue && 'New value: '}
          {isEmail ? (
            <a href={`mailto:${replacementValue?.actualValue ?? value}`}>{replacementValue?.actualValue ?? value}</a>
          ) : (
            (getFormattedNewValue(replacementValue?.actualValue) ?? value)
          )}
        </div>
      </div>
      <div className="flex justify-center items-center">
        {status !== 'success' || !!replacementValue?.actualValue ? (
          <WppTooltip config={{ allowHTML: true, placement: 'top' }}>
            <img
              src={ResyncIcon}
              alt="changed"
              style={{ maxWidth: '16px' }}
              className={clsx(!replacementValue?.actualValue?.length && 'hidden')}
            />
            {status === 'warning' ? (
              <img
                src={WarningIcon}
                alt="warning"
                style={{ maxWidth: '16px' }}
                className={clsx(!!replacementValue?.actualValue?.length && 'hidden')}
              />
            ) : (
              <img
                src={ErrorIcon}
                alt="error"
                style={{ maxWidth: '16px' }}
                className={clsx(!!replacementValue?.actualValue?.length && 'hidden')}
              />
            )}
            <div
              slot="tooltip-content"
              className={clsx(styles.statusTooltip, {
                [styles.statusWarning]: status === 'warning',
                [styles.statusError]: status === 'error',
              })}
            >
              <span className="text-[14px]">
                {!replacementValue?.actualValue &&
                  (status === 'error' ? (
                    <span>Sync Error</span>
                  ) : (
                    <>
                      <span>{warningMessage?.status}</span>
                      <br />
                      <span className="italic">{warningMessage?.details}</span>
                    </>
                  ))}
              </span>

              <span className="text-[14px]">{!!replacementValue?.actualValue && <span>Value will change</span>}</span>
            </div>
          </WppTooltip>
        ) : (
          <img className="max-w-[16px]" src={SuccessIcon} alt="success" />
        )}
      </div>
    </div>
  )
}

const AttachmentButton = ({
  label,
  documentId,
  projectName,
  isDisabled,
}: {
  label: string
  documentId?: string
  projectName: string
  isDisabled?: boolean
}) => (
  <div
    className={clsx(styles.attachmentButton, { [styles.btnDisabled]: !!isDisabled })}
    onClick={() => {
      documentId && window.open(`${config[API_TYPES.BRIEF_API_URL]}/projects/documents/${documentId}`)

      trackAnalytics({
        type: AnalyticsActionType.action,
        payload: ANALYTICS_EVENTS.DETAILED_ITEM_ATTACHMENTS_BUTTON_CLICKED(projectName, label),
      })
    }}
  >
    <WppIconAttach color="black" />
    <TruncatedText maxWidth="160px" className="font-bold text-[12px] ml-2" text={label} />
  </div>
)
